import axios from "axios";
import { Check, MoreVertical } from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import Heading from "../../../components/AdminSideComponents/Heading";
import EditManagerDrawer from "../../../components/AdminSideComponents/managers/EditManagerDrawer";
import { PERMISSION_GROUPS } from "../../../configs/sidebarMenus.config";
import {
  confirmAction,
  showError,
  showSuccess,
} from "../../../utils/toastUtils";

const Managers = () => {
  const [managers, setManagers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [actionOpen, setActionOpen] = useState(null);
  const [editingManager, setEditingManager] = useState(null);

  const [permissionModal, setPermissionModal] = useState(null);
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  const fetchManagers = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/admins/get-all-branch-managers`,
      );
      setManagers(res.data?.data || []);
      setFiltered(res.data?.data || []);
    } catch {
      showError("Failed to fetch managers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  const handleSearch = (term) => {
    if (!term.trim()) {
      setFiltered(managers);
      return;
    }

    const lower = term.toLowerCase();
    setFiltered(
      managers.filter(
        (m) =>
          m.name?.toLowerCase().includes(lower) ||
          m.email?.toLowerCase().includes(lower) ||
          m.phoneNumber?.includes(lower),
      ),
    );
  };

  const handleDelete = async (manager) => {
    const confirmed = await confirmAction({
      title: "Delete Manager?",
      text: "This action cannot be undone",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/admins/delete/${manager._id}`,
      );
      showSuccess("Manager deleted");
      fetchManagers();
    } catch {
      showError("Failed to delete manager");
    }
  };

  const openPermissionEditor = (manager) => {
    setPermissionModal(manager);
    setSelectedPermissions(manager.permissions || []);
  };

  return (
    <div className="bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <div className="py-2">
        <Breadcrumb title1="Organization" title2="Managers" />
      </div>

      <div className="mb-6 bg-white rounded-xl shadow-lg p-6 border border-gray-100">
        <Heading
          onSearch={handleSearch}
          showButton
          buttonName="Add Manager"
          onButtonClick={() => setEditingManager({})}
        />

        <div className="mt-6 space-y-3">
          {loading ? (
            <div className="py-16 text-center text-gray-500">
              Loading managers...
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-500">
              No managers found
            </div>
          ) : (
            filtered.map((manager) => (
              <div
                key={manager._id}
                className="relative flex items-center gap-6 px-5 py-4 rounded-lg border border-gray-200 hover:border-indigo-200 hover:bg-indigo-50/30 transition"
              >
                <div className="w-12 h-12 rounded-full bg-indigo-600 flex items-center justify-center text-white font-semibold">
                  {manager.name?.[0]?.toUpperCase()}
                </div>

                <div className="flex-1">
                  <div className="flex justify-between">
                    <h3 className="font-medium text-gray-900">
                      {manager.name}
                    </h3>
                    <span className="text-xs text-gray-500">
                      {manager.assignedBranch?.branchName || "—"}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-1 flex gap-6">
                    <span>{manager.email}</span>
                    <span>{manager.phoneNumber || "—"}</span>
                  </div>
                </div>

                <div className="relative">
                  <button
                    onClick={() =>
                      setActionOpen((p) =>
                        p === manager._id ? null : manager._id,
                      )
                    }
                    className="p-2 rounded-full hover:bg-gray-200"
                  >
                    <MoreVertical size={18} />
                  </button>

                  {actionOpen === manager._id && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setActionOpen(null)}
                      />
                      <div className="absolute right-0 top-10 w-56 bg-white border rounded-xl shadow-xl z-20 py-2">
                        <button
                          onClick={() => {
                            openPermissionEditor(manager);
                            setActionOpen(null);
                          }}
                          className="w-full px-5 py-2.5 text-left hover:bg-indigo-50"
                        >
                          Manage Permissions
                        </button>
                        <button
                          onClick={() => {
                            setEditingManager(manager);
                            setActionOpen(null);
                          }}
                          className="w-full px-5 py-2.5 text-left hover:bg-blue-50 text-blue-700"
                        >
                          Edit Manager
                        </button>
                        <button
                          onClick={() => handleDelete(manager)}
                          className="w-full px-5 py-2.5 text-left hover:bg-red-50 text-red-700"
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {permissionModal && (
        <PermissionModal
          manager={permissionModal}
          initialPermissions={selectedPermissions}
          onClose={() => setPermissionModal(null)}
          onSuccess={fetchManagers}
        />
      )}

      {editingManager !== null && (
        <EditManagerDrawer
          isOpen={!!editingManager}
          manager={editingManager}
          onClose={() => setEditingManager(null)}
          onSuccess={() => {
            setEditingManager(null);
            fetchManagers();
          }}
        />
      )}
    </div>
  );
};

const PermissionModal = ({
  manager,
  initialPermissions = [],
  onClose,
  onSuccess,
}) => {
  const [permissions, setPermissions] = useState(initialPermissions);
  const [isSaving, setIsSaving] = useState(false);

  const togglePermission = (key) => {
    setPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/admins/assign-permissions/${manager._id}`,
        { permissions },
      );

      showSuccess("Permissions updated");
      onSuccess();
      onClose();
    } catch (err) {
      showError(err?.response?.data?.message || "Failed to update permissions");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-3xl rounded-2xl shadow-2xl border border-[#F7BFAD]/40 overflow-hidden">
        {/* HEADER */}
        <div className="px-8 py-6 bg-accentTextColor/20 backdrop-blur-md border-b border-accentTextColor/30 relative overflow-hidden">
          <h2 className="text-xl font-semibold text-gray-900 relative z-10">
            Manage Permissions
          </h2>
          <p className="text-sm text-gray-700 mt-1 relative z-10">
            Control access for{" "}
            <span className="font-medium text-accentTextColor">
              {manager.name}
            </span>
          </p>
        </div>

        {/* BODY */}
        <div className="px-8 py-6 max-h-[65vh] overflow-y-auto space-y-8">
          {Object.entries(PERMISSION_GROUPS).map(([group, perms]) => (
            <div
              key={group}
              className="rounded-xl border border-gray-200 bg-white"
            >
              {/* GROUP HEADER */}
              <div className="px-5 py-3 border-b bg-[#FFF7F3]/60">
                <h4 className="text-sm font-semibold text-gray-700">{group}</h4>
              </div>

              {/* PERMISSIONS */}
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
                {perms.map((p) => {
                  const active = permissions.includes(p);

                  return (
                    <button
                      type="button"
                      key={p}
                      onClick={() => togglePermission(p)}
                      className={`text-left px-4 py-3 rounded-lg border transition
                        ${
                          active
                            ? "bg-[#FFF7F3] border-[#f0653a] text-[#f0653a]"
                            : "border-gray-200 text-gray-700 hover:border-[#F7BFAD]"
                        }
                      `}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium capitalize">
                          {p.replace(/-/g, " ")}
                        </span>

                        {active && (
                          <Check size={16} className="text-[#f0653a]" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className="flex justify-end gap-4 px-8 py-5 border-t bg-white">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-6 py-2.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium transition disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving}
            className="relative min-w-[160px] px-6 py-2.5 rounded-lg bg-[#f0653a] hover:opacity-90 text-white font-medium shadow-sm transition flex items-center justify-center disabled:opacity-80"
          >
            {isSaving ? (
              <span className="h-5 w-5 border-2 border-white/70 border-t-transparent rounded-full animate-spin" />
            ) : (
              "Save Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Managers;
